import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useScrollRestoration from "../hooks/useScrollRestoration";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { apiClient, useAuth } from "../App";
import { useAdminControls } from "../AdminMode";
import {
  FileText, Folder, FolderOpen, Search, ExternalLink, Download,
  AlertCircle, ShieldCheck, FileImage, FileVideo, Music as MusicIcon,
  FileSpreadsheet, File, RefreshCw,
} from "lucide-react";

const formatBytes = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const getFileIcon = (mimeType) => {
  const mt = mimeType || "";
  if (mt.startsWith("image/")) return FileImage;
  if (mt.startsWith("video/")) return FileVideo;
  if (mt.startsWith("audio/")) return MusicIcon;
  if (mt.includes("spreadsheet") || mt.includes("excel") || mt.includes("csv")) return FileSpreadsheet;
  if (mt === "application/vnd.google-apps.folder") return Folder;
  return File;
};

const formatMime = (mt) => {
  if (!mt) return "";
  if (mt.startsWith("application/vnd.google-apps.")) {
    return mt.replace("application/vnd.google-apps.", "Google ").replace(/^./, (c) => c.toUpperCase());
  }
  const parts = mt.split("/");
  return parts[1] ? parts[1].toUpperCase() : mt;
};

const Documents = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { showAdminControls } = useAdminControls();
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [driveError, setDriveError] = useState(null);
  const [search, setSearch] = useState("");
  // iter-86: preserve scroll when admin views a file and returns
  useScrollRestoration({ ready: !loadingFiles });

  const loadFolders = async () => {
    try {
      const res = await apiClient.get("/document-folders");
      setFolders(res.data || []);
      if ((res.data || []).length > 0 && !selectedFolder) {
        selectFolder(res.data[0]);
      }
    } catch (e) {
      toast.error("Failed to load documents");
    }
  };

  const selectFolder = async (folder) => {
    setSelectedFolder(folder);
    setFiles([]);
    setDriveError(null);
    setLoadingFiles(true);
    try {
      const res = await apiClient.get(`/document-folders/${folder.id}/files`);
      setFiles(res.data.files || []);
      if (res.data.error) setDriveError(res.data.error);
    } catch (e) {
      setDriveError(e.response?.data?.detail || "Failed to load files");
    } finally {
      setLoadingFiles(false);
    }
  };

  useEffect(() => { loadFolders(); }, []); // eslint-disable-line

  const filteredFiles = search
    ? files.filter((f) => (f.name || "").toLowerCase().includes(search.toLowerCase()))
    : files;

  return (
    <div className="space-y-6 animate-fade-in min-w-0 max-w-full overflow-x-hidden" data-testid="documents-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight">Documents</h1>
            <p className="text-muted-foreground mt-1">Band-shared files — sheet music, minutes, forms and more.</p>
          </div>
        </div>
        {showAdminControls && (
          <Button variant="outline" onClick={() => navigate("/admin/documents")} data-testid="manage-folders-btn">
            Manage Folders
          </Button>
        )}
      </div>

      {folders.length === 0 ? (
        <Card className="glass-card">
          <CardContent className="p-12 text-center">
            <Folder className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-50" />
            <p className="text-muted-foreground">
              {showAdminControls
                ? "No document folders yet. Connect a Google Drive folder to get started."
                : "No documents have been shared with you yet."}
            </p>
            {showAdminControls && (
              <Button className="mt-4" onClick={() => navigate("/admin/documents")} data-testid="empty-setup-btn">
                Set up Documents
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
          {/* Folder list */}
          <Card className="glass-card h-fit">
            <CardHeader className="pb-3 border-b border-white/5">
              <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">Folders</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              {folders.map((f) => {
                const isSelected = selectedFolder?.id === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => selectFolder(f)}
                    className={`w-full text-left rounded-lg p-3 transition-colors flex items-start gap-3 ${
                      isSelected ? "bg-primary/10 border border-primary/30" : "hover:bg-white/5"
                    }`}
                    data-testid={`folder-item-${f.id}`}
                  >
                    {isSelected ? <FolderOpen className="w-4 h-4 mt-0.5" style={{ color: f.color || "#E6B800" }} /> : <Folder className="w-4 h-4 mt-0.5" style={{ color: f.color || "#E6B800" }} />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium break-words">{f.name}</span>
                        {f.visibility === "committee" && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-amber-500/10 border-amber-500/30 text-amber-400">
                            <ShieldCheck className="w-2.5 h-2.5 mr-0.5" />Committee
                          </Badge>
                        )}
                      </div>
                      {f.description && (
                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{f.description}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* File list */}
          <Card className="glass-card">
            <CardHeader className="pb-3 border-b border-white/5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="font-heading text-xl">{selectedFolder?.name || "Files"}</CardTitle>
                  {selectedFolder?.description && (
                    <p className="text-sm text-muted-foreground mt-0.5">{selectedFolder.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search files..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-8 h-9 w-48"
                      data-testid="file-search"
                    />
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => selectedFolder && selectFolder(selectedFolder)} title="Refresh" data-testid="refresh-files-btn">
                    <RefreshCw className={`w-4 h-4 ${loadingFiles ? "animate-spin" : ""}`} />
                  </Button>
                  {selectedFolder && (
                    <a href={selectedFolder.drive_folder_url} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" data-testid="open-folder-in-drive-btn">
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open in Drive
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {driveError && (
                <div className="m-4 p-4 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-3" data-testid="drive-error">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-red-400">Unable to load files from Google Drive</p>
                    <p className="text-muted-foreground mt-1">{driveError}</p>
                    {showAdminControls && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Tip: make sure the folder is shared as &ldquo;Anyone with the link can view&rdquo;.
                      </p>
                    )}
                  </div>
                </div>
              )}
              {loadingFiles ? (
                <div className="p-12 text-center text-muted-foreground">Loading files…</div>
              ) : filteredFiles.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground" data-testid="no-files">
                  <File className="w-10 h-10 mx-auto opacity-40 mb-2" />
                  {search ? "No files match your search." : driveError ? "" : "This folder is empty."}
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {filteredFiles.map((f) => {
                    const Icon = getFileIcon(f.mimeType);
                    return (
                      <div key={f.id} className="flex items-start gap-4 p-4 hover:bg-white/[0.02] transition-colors min-w-0" data-testid={`file-row-${f.id}`}>
                        <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0 break-words">
                          <div className="font-medium break-words">{f.name}</div>
                          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                            <span>{formatMime(f.mimeType)}</span>
                            {f.size_bytes != null && <><span>•</span><span>{formatBytes(f.size_bytes)}</span></>}
                            {f.modifiedTime && <><span>•</span><span>Modified {new Date(f.modifiedTime).toLocaleDateString()}</span></>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {f.webContentLink && (
                            <a href={f.webContentLink} target="_blank" rel="noreferrer">
                              <Button size="icon" variant="ghost" title="Download" data-testid={`file-download-${f.id}`}>
                                <Download className="w-4 h-4" />
                              </Button>
                            </a>
                          )}
                          {f.webViewLink && (
                            <a href={f.webViewLink} target="_blank" rel="noreferrer">
                              <Button size="sm" variant="outline" data-testid={`file-open-${f.id}`}>
                                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                                Open
                              </Button>
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Documents;
